import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, Map, Clock, CreditCard, Wrench,PenTool, Car, Check, Star, Download, Menu, X } from 'lucide-react';
import { Search, Filter, ShoppingCart, Package } from 'lucide-react';
import { MiniParkingScene } from './components/MiniParkingScene';

// Enhanced Hero Section with full-screen 3D background
export const EnhancedHeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
      {/* 3D Background - CarScene3D is rendered in the ZoneXLanding component as a full-screen background */}

      {/* Content */}
      <div className="container mx-auto px-4 z-10 py-16">
        <div className="max-w-2xl backdrop-blur-sm bg-black/30 p-8 rounded-xl">
          <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6 animate-fadeIn">
            Urban Mobility <span className="text-[#FC3F3F]">Redefined</span> for The Modern Driver
          </h1>
          <p className="text-lg md:text-xl text-gray-300 mb-8 animate-fadeIn animation-delay-200">
         Book parking spots in advance, connect with certified mechanics on demand, and access quality spare parts—all from one integrated platform. ZoneX delivers a seamless vehicle management experience designed for today's urban lifestyle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn animation-delay-300">
            <button className="bg-[#FC3F3F] text-white px-8 py-3 rounded-full hover:bg-[#FC3F3F]/90 transition-all duration-300 flex items-center justify-center">
              Get Started
            </button>
            <button className="border border-white text-white px-8 py-3 rounded-full hover:bg-white/10 transition-all duration-300 flex items-center justify-center">
              Watch Demo
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Enhanced Parking Section with mini 3D scene
export const EnhancedParkingSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gray-900 relative overflow-hidden"
      id="parking-section"
    >
      {/* Accent shapes */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-[#FC3F3F]/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Smart Parking Solutions</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Never circle for a spot again. ZoneX helps you find and reserve parking in seconds.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
            <div className="bg-black rounded-xl overflow-hidden">
              {/* Integrate 3D parking visualization */}
              <MiniParkingScene containerHeight="400px" showMechanic={false} />
            </div>
          </div>

          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}>
            <h3 className="text-2xl font-bold mb-6">Find Perfect Parking in Seconds</h3>
            <p className="text-gray-400 mb-8">
              Our intelligent system matches you with the ideal parking spot based on your location, vehicle type, and duration needs.
            </p>

            <ul className="space-y-4 mb-8">
              {[
                "Real-time availability updates",
                "Price comparison across locations",
                "Reservation management system",
                "Favorite locations for quick access",
                "Contactless check-in and payment"
              ].map((item, index) => (
                <li key={index} className="flex items-center">
                  <Check className="w-5 h-5 text-[#FC3F3F] mr-2 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <button className="bg-[#FC3F3F] text-white px-6 py-3 rounded-full hover:bg-[#FC3F3F]/90 transition-all duration-300 flex items-center">
              Explore Parking Features <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Enhanced Spare Parts Section - Professional Service-Focused Version
export const EnhancedSparePartsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // Intersection Observer for section visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Service features that highlight company capabilities
  const features = [
    {
      icon: <ShoppingCart className="w-8 h-8 text-zonex-red" />,
      title: "Genuine OEM Parts",
      description: "Access certified genuine parts sourced directly from manufacturers, ensuring optimal performance and longevity for your vehicle."
    },
    {
      icon: <Clock className="w-8 h-8 text-zonex-red" />,
      title: "Express Delivery",
      description: "Receive critical parts when you need them with our same-day and next-day delivery options for urban locations."
    },
    {
      icon: <Car className="w-8 h-8 text-zonex-red" />,
      title: "Vehicle Compatibility",
      description: "Our advanced system ensures you only receive parts that are perfectly compatible with your specific vehicle make and model."
    },
    {
      icon: <Package className="w-8 h-8 text-zonex-red" />,
      title: "Warranty Guaranteed",
      description: "Every part comes with a manufacturer warranty and our ZoneX quality guarantee for complete peace of mind and reliability."
    }
  ];

  return (
    <section
      ref={sectionRef}
      className="py-20 relative overflow-hidden bg-gradient-to-br from-zonex-white via-[#F8F9FA] to-zonex-white"
      id="spare-parts-section"
    >
      {/* Advanced Background with Modernized Light Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Light Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#F8F9FA]/50 via-[#FC3F3F]/5 to-transparent opacity-50"></div>

        {/* Animated Particles - Lighter Style */}
        <div className="absolute inset-0 z-0">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-[#FC3F3F]/10 rounded-full animate-particle"
              style={{
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 10}s`
              }}
            />
          ))}
        </div>

        {/* Geometric Light Shapes */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-[#FC3F3F]/5 rounded-full blur-2xl animate-float-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#FC3F3F]/10 rounded-full blur-3xl animate-float-slow-reverse"></div>
        </div>
      </div>

      {/* Animated Car Silhouettes with Refined Styling */}
      <div className="absolute bottom-0 w-full h-32 opacity-30 overflow-hidden">
        <div className="car-silhouette car-1 bg-gradient-to-r from-[#FC3F3F] to-[#FF6B6B] shadow-red-glow"></div>
        <div className="car-silhouette car-2 bg-gradient-to-r from-[#FF6B6B] to-[#FC3F3F] shadow-red-glow"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header with Modern Styling */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 text-zonex-gray-900 bg-clip-text">
            Premium Automotive Spare Parts Services
          </h2>
          <div className="w-20 h-1 bg-[#FC3F3F] mx-auto mb-6 rounded-full shadow-red-glow"></div>
          <p className="text-zonex-gray-700 max-w-2xl mx-auto">
            ZoneX provides an extensive catalog of high-quality automotive components with seamless delivery and expert installation options. Our comprehensive parts service ensures your vehicle maintains peak performance with minimal downtime.
          </p>
        </div>

        {/* Main Content - Service Features Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="bg-white rounded-xl p-6 border border-[#FC3F3F]/20 shadow-lg hover:shadow-xl 
                       transition-all duration-300 transform hover:-translate-y-2 
                       bg-gradient-to-br from-white to-[#FC3F3F]/5"
            >
              <div className="flex items-start mb-4">
                <div className="p-3 bg-[#FC3F3F]/10 rounded-lg mr-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-zonex-gray-900 mt-2">{feature.title}</h3>
              </div>
              <p className="text-zonex-gray-700 ml-16">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Process Flow Section - Completely Redesigned */}
        <div className={`mt-16 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
          <h3 className="text-2xl font-bold text-center mb-16 text-zonex-gray-900">How Our Spare Parts Service Works</h3>
          
          <div className="max-w-4xl mx-auto px-4">
            <div className="relative">
              {/* Connection line */}
              <div className="hidden md:block absolute top-20 left-0 w-full h-0.5 bg-gradient-to-r from-[#FC3F3F]/20 via-[#FC3F3F] to-[#FC3F3F]/20" style={{zIndex: 0}}></div>
              
              {/* Two-step process */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                {/* Step 1 */}
                <div className="relative">
                  {/* Large number backdrop */}
                  <div className="absolute -top-16 -left-4 opacity-10">
                    <span className="text-9xl font-black text-[#FC3F3F]">1</span>
                  </div>
                  
                  <div className="flex flex-col items-center text-center relative">
                    {/* Circle icon with gradient */}
                    <div className="flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-[#FC3F3F] to-[#FF6B6B] shadow-lg mb-6 relative z-10">
                      <Search className="w-8 h-8 text-white" />
                    </div>
                    
                    <h4 className="text-xl font-bold mb-3 text-zonex-gray-900">Search & Select</h4>
                    <p className="text-zonex-gray-700">Use our intuitive platform to find the exact parts your vehicle needs with our comprehensive compatibility verification system.</p>
                  </div>
                </div>
                
                {/* Step 2 */}
                <div className="relative">
                  {/* Large number backdrop */}
                  <div className="absolute -top-16 -left-4 opacity-10">
                    <span className="text-9xl font-black text-[#FC3F3F]">2</span>
                  </div>
                  
                  <div className="flex flex-col items-center text-center relative">
                    {/* Circle icon with gradient */}
                    <div className="flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-[#FC3F3F] to-[#FF6B6B] shadow-lg mb-6 relative z-10">
                      <Package className="w-8 h-8 text-white" />
                    </div>
                    
                    <h4 className="text-xl font-bold mb-3 text-zonex-gray-900">Fast Delivery</h4>
                    <p className="text-zonex-gray-700">We process your order immediately and arrange rapid delivery through our optimized logistics network to minimize vehicle downtime.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Business Benefits Section */}
        <div className={`mt-16 bg-gradient-to-r from-[#FC3F3F]/10 to-transparent p-8 rounded-xl border border-[#FC3F3F]/20 
                        transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
          
          <div className="md:flex items-center">
            <div className="md:w-2/3 pr-8">
              <h3 className="text-2xl font-bold mb-4 text-zonex-gray-900">Why Choose ZoneX Spare Parts Services?</h3>
              <div className="w-16 h-1 bg-[#FC3F3F] mb-6 rounded-full shadow-red-glow"></div>
              <ul className="space-y-3">
                {[
                  "Extensive inventory covering 95% of vehicle makes and models on Indian roads",
                  "Quality assurance with manufacturer warranties and ZoneX guarantees",
                  "Transparent pricing with no hidden costs or markup surprises",
                  "Fast delivery options to minimize vehicle downtime",
                  "Real-time inventory management ensuring availability of critical components"
                ].map((item, index) => (
                  <li key={index} className="flex items-center">
                    <div className="mr-3 p-1 bg-[#FC3F3F]/20 rounded-full flex-shrink-0">
                      <Check className="w-4 h-4 text-[#FC3F3F]" />
                    </div>
                    <span className="text-zonex-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="md:w-1/3 mt-6 md:mt-0">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#FC3F3F]/20 to-transparent rounded-xl transform rotate-3"></div>
                <img 
                  src="/spares.jpg" 
                  alt="ZoneX Spare Parts Quality Assurance" 
                  className="rounded-xl shadow-lg relative z-10 border-2 border-white" 
                />
                <div className="absolute -bottom-4 -right-4 bg-[#FC3F3F] text-white p-2 rounded-lg shadow-lg z-20">
                  <Star className="w-6 h-6 fill-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className={`mt-12 text-center transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
          <a
            href="https://zonexsparesweb.web.app/"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-[#FC3F3F] to-[#FF6B6B] text-white 
                       px-8 py-4 rounded-full 
                       hover:from-[#FF6B6B] hover:to-[#FC3F3F] 
                       transition-all duration-300 
                       inline-flex items-center justify-center
                       shadow-lg hover:shadow-xl hover:shadow-[#FC3F3F]/30 
                       transform hover:-translate-y-1"
          >
            Explore Our Parts Catalog <ChevronRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </div>

      {/* Global Styles for Animations */}
      <style>{`
        @keyframes particle {
          0% { 
            transform: scale(1) translateY(0);
            opacity: 0.3;
          }
          50% { 
            transform: scale(1.5) translateY(-50px);
            opacity: 0.5;
          }
          100% { 
            transform: scale(1) translateY(0);
            opacity: 0.2;
          }
        }

        @keyframes float-slow {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }

        @keyframes float-slow-reverse {
          0% { transform: translateY(0px); }
          50% { transform: translateY(20px); }
          100% { transform: translateY(0px); }
        }

        @keyframes carMove1 {
          0% { transform: translateX(-100px); }
          100% { transform: translateX(calc(100vw + 100px)); }
        }

        @keyframes carMove2 {
          0% { transform: translateX(calc(100vw + 100px)); }
          100% { transform: translateX(-100px); }
        }

        .animate-particle {
          animation: particle linear infinite;
        }

        .animate-float-slow {
          animation: float-slow 10s ease-in-out infinite;
        }

        .animate-float-slow-reverse {
          animation: float-slow-reverse 8s ease-in-out infinite;
        }

        .car-silhouette {
          position: absolute;
          width: 80px;
          height: 30px;
          border-radius: 5px;
          box-shadow: 0 0 15px rgba(252, 63, 63, 0.5);
        }

        .car-1 {
          bottom: 10px;
          animation: carMove1 15s linear infinite;
        }

        .car-2 {
          bottom: 50px;
          animation: carMove2 12s linear infinite;
        }

        .shadow-red-glow {
          box-shadow: 0 0 15px rgba(252, 63, 63, 0.4);
        }
      `}</style>
    </section>
  );
};





export const EnhancedSolutionsSection = () => {
  const [activeProduct, setActiveProduct] = useState(0);
  const sectionRef = useRef(null);

  const products = [
    {
      name: "Mechanics",
      image: "/mechanics.jpg", // Replace with actual image path
      description: "Connect vehicle owners with trusted mechanics for repairs, maintenance, and parts through our integrated service platform.",
      icon: <PenTool className="w-6 h-6 text-zonex-red" />
    },
    {
      name: "Access Control",
      image: "/control.jpg", // Replace with actual image path
      description: "Secure entry management for various facilities with RFID, Bluetooth, and FASTag options tailored to specific environments.",
      icon: <CreditCard className="w-6 h-6 text-zonex-red" />
    },
    {
      name: "Parking",
      image: "/parking.jpg", // Replace with actual image path
      description: "Comprehensive parking management solutions with digital apps, smart barriers, and AI-powered monitoring for streamlined operations.",
      icon: <Map className="w-6 h-6 text-zonex-red" />
    },
  ];

  const handleProductClick = (index) => {
    setActiveProduct(index);
  };

  // Animation effect for section entry
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef} 
      id="solutions-section" 
      className="py-20 bg-gradient-to-b from-zonex-darkGray to-zonex-lightGray relative overflow-hidden"
    >
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-zonex-red/30 to-transparent"></div>
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-zonex-red/15 rounded-full blur-3xl"></div>
      
      {/* Animated particles */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-3 h-3 rounded-full bg-zonex-red/30 animate-float" style={{ animationDelay: '0s' }}></div>
        <div className="absolute top-1/3 left-1/2 w-2 h-2 rounded-full bg-zonex-red/40 animate-float" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute bottom-1/3 right-1/4 w-4 h-4 rounded-full bg-zonex-red/20 animate-float" style={{ animationDelay: '1.2s' }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Solutions</h2>
          <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Transforming mobility with our comprehensive suite of integrated services
          </p>
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left side: Product list */}
          <div className="space-y-4">
            {products.map((product, index) => (
              <div
                key={index}
                className={`product-card cursor-pointer ${
                  activeProduct === index 
                    ? 'active-product' 
                    : ''
                }`}
                onClick={() => handleProductClick(index)}
              >
                <div className="flex items-start justify-between">
                  <div className="product-icon">
                    {product.icon}
                  </div>
                  <ChevronRight className="product-arrow" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
                <p className="product-description">
                  {product.description}
                </p>
              </div>
            ))}
          </div>

          {/* Right side: Image display */}
          <div>
            <div className="image-showcase">
              {products.map((product, index) => (
                <div 
                  key={index} 
                  className={`image-item ${activeProduct === index ? 'active-image' : ''}`}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="image-element"
                  />
                  <div className="image-overlay"></div>
                  
                  {/* Image caption */}
                  <div className="image-caption">
                    <h3 className="text-xl font-bold text-white mb-2">{product.name}</h3>
                    <div className="caption-bar"></div>
                    <p className="text-white/80 text-sm">
                      {product.description}
                    </p>
                  </div>
                </div>
              ))}
              
              {/* Image navigation dots */}
              <div className="image-nav">
                {products.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleProductClick(index)}
                    className={`nav-dot ${activeProduct === index ? 'active-dot' : ''}`}
                    aria-label={`View ${products[index].name}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Updated CSS for images */}
      <style dangerouslySetInnerHTML={{__html: `
        /* Product cards styling */
        .product-card {
          background-color: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 20px;
          transition: all 0.3s ease;
          height: 130px;
          display: flex;
          flex-direction: column;
        }
        
        .product-card:hover {
          background-color: rgba(252, 63, 63, 0.1);
          border-color: rgba(252, 63, 63, 0.5);
          transform: translateX(-5px);
        }
        
        .product-card.active-product {
          background-color: rgba(252, 63, 63, 1);
          color: white;
          border-color: rgba(252, 63, 63, 1);
          transform: translateX(-5px);
          box-shadow: 0 10px 25px -5px rgba(252, 63, 63, 0.3),
                      0 0 15px -5px rgba(252, 63, 63, 0.5);
        }
        
        .product-icon {
          background-color: rgba(252, 63, 63, 0.1);
          padding: 10px;
          border-radius: 8px;
          margin-bottom: 10px;
          display: inline-block;
        }
        
        .active-product .product-icon {
          background-color: rgba(255, 255, 255, 0.2);
        }
        
        .product-arrow {
          width: 20px;
          height: 20px;
          color: rgba(252, 63, 63, 1);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .product-card:hover .product-arrow {
          opacity: 1;
        }
        
        .active-product .product-arrow {
          opacity: 1;
          color: white;
        }
        
        .product-description {
          font-size: 14px;
          color: #d1d1d1;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .active-product .product-description {
          color: rgba(255, 255, 255, 0.9);
        }
        
        /* Image showcase styling */
        .image-showcase {
          position: relative;
          height: 580px;
          border-radius: 12px;
          overflow: hidden;
          border: 3px solid rgba(252, 63, 63, 0.3);
          box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.3);
        }
        
        .image-item {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.5s ease, transform 0.5s ease;
          transform: scale(0.95);
        }
        
        .image-item.active-image {
          opacity: 1;
          transform: scale(1);
        }
        
        .image-element {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .image-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
        }
        
        .image-caption {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          padding: 20px;
          background-color: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(5px);
          border-top: 1px solid rgba(252, 63, 63, 0.3);
        }
        
        .caption-bar {
          width: 40px;
          height: 3px;
          background-color: rgba(252, 63, 63, 1);
          margin-bottom: 8px;
          border-radius: 3px;
          box-shadow: 0 0 10px rgba(252, 63, 63, 0.5);
        }
        
        .image-nav {
          position: absolute;
          bottom: 20px;
          right: 20px;
          display: flex;
          gap: 8px;
        }
        
        .nav-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.5);
          transition: all 0.3s ease;
          border: none;
          padding: 0;
        }
        
        .nav-dot:hover {
          background-color: rgba(255, 255, 255, 0.8);
        }
        
        .nav-dot.active-dot {
          background-color: rgba(252, 63, 63, 1);
          width: 24px;
          border-radius: 6px;
        }
        
        /* Animation */
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
          100% { transform: translateY(0px); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .shadow-red-glow {
          box-shadow: 0 0 15px rgba(252, 63, 63, 0.4);
        }
      `}} />
    </section>
  );
};